import { queryOptions } from '@tanstack/react-query'
import { api, publicApi } from './http'
import type {
  DailyUptime,
  Dashboard,
  TargetDaily,
  PingResult,
  PublicService,
  PublicTargetDetail,
  ShareService,
  Service,
  ServiceDetail,
  StatsWindow,
  Target,
  TimelinePoint,
  UptimeStats,
} from './types'

/**
 * Zive obrazovky se obnovuji samy pres refetchInterval.
 *
 * Petisekundovy interval odpovida nejkratsi povolene perioda mereni — casteji
 * by se stejne nebylo co dozvedet. SSE ani WebSocket zatim nejsou potreba:
 * data jsou mala, dotaz levny a polling prezije vypadek spojeni bez logiky
 * na znovupripojeni.
 */
export const LIVE_REFETCH_MS = 5_000

export const dashboardQuery = () =>
  queryOptions({
    queryKey: ['dashboard'],
    queryFn: async () => (await api.get<Dashboard>('/dashboard')).data,
    refetchInterval: LIVE_REFETCH_MS,
  })

export const servicesQuery = () =>
  queryOptions({
    queryKey: ['services'],
    queryFn: async () => (await api.get<Service[]>('/services')).data,
  })

export const serviceDetailQuery = (serviceId: string) =>
  queryOptions({
    queryKey: ['services', serviceId],
    queryFn: async () => (await api.get<ServiceDetail>(`/services/${serviceId}`)).data,
    refetchInterval: LIVE_REFETCH_MS,
  })

/** Denni pruhy vsech cilu sluzby — jeden dotaz misto dotazu na cil. */
export const serviceDailyQuery = (serviceId: string, days = 30) =>
  queryOptions({
    queryKey: ['services', serviceId, 'daily', days],
    queryFn: async () =>
      (await api.get<TargetDaily[]>(`/services/${serviceId}/daily`, { params: { days } })).data,
    refetchInterval: LIVE_REFETCH_MS,
  })

export const targetQuery = (targetId: string) =>
  queryOptions({
    queryKey: ['targets', targetId],
    queryFn: async () => (await api.get<Target>(`/targets/${targetId}`)).data,
    refetchInterval: LIVE_REFETCH_MS,
  })

export const targetStatsQuery = (targetId: string, window: StatsWindow) =>
  queryOptions({
    queryKey: ['targets', targetId, 'stats', window],
    queryFn: async () =>
      (await api.get<UptimeStats>(`/targets/${targetId}/stats`, { params: { window } })).data,
    refetchInterval: LIVE_REFETCH_MS,
  })

export const targetTimelineQuery = (targetId: string, window: StatsWindow) =>
  queryOptions({
    queryKey: ['targets', targetId, 'timeline', window],
    queryFn: async () =>
      (await api.get<TimelinePoint[]>(`/targets/${targetId}/timeline`, { params: { window } })).data,
    refetchInterval: LIVE_REFETCH_MS,
  })

/** Denni pruh za 90 dnu — stejny pohled jako na verejne status strance. */
export const targetDailyQuery = (targetId: string, days = 90) =>
  queryOptions({
    queryKey: ['targets', targetId, 'daily', days],
    queryFn: async () =>
      (await api.get<DailyUptime[]>(`/targets/${targetId}/daily`, { params: { days } })).data,
    refetchInterval: LIVE_REFETCH_MS,
  })

export const targetResultsQuery = (targetId: string, limit = 50) =>
  queryOptions({
    queryKey: ['targets', targetId, 'results', limit],
    queryFn: async () =>
      (await api.get<PingResult[]>(`/targets/${targetId}/results`, { params: { limit } })).data,
    refetchInterval: LIVE_REFETCH_MS,
  })

// --- Verejne stranky --------------------------------------------------------

export const publicServiceQuery = (publicId: string) =>
  queryOptions({
    queryKey: ['public', 'status', publicId],
    queryFn: async () => (await publicApi.get<PublicService>(`/status/${publicId}`)).data,
    refetchInterval: LIVE_REFETCH_MS,
  })

/** Sdilena read-only podoba detailu sluzby (magic link). */
export const shareQuery = (token: string) =>
  queryOptions({
    queryKey: ['share', token],
    queryFn: async () => (await publicApi.get<ShareService>(`/share/${token}`)).data,
    refetchInterval: LIVE_REFETCH_MS,
  })

export const publicTargetQuery = (targetId: string) =>
  queryOptions({
    queryKey: ['public', 'targets', targetId],
    queryFn: async () => (await publicApi.get<PublicTargetDetail>(`/targets/${targetId}`)).data,
    refetchInterval: LIVE_REFETCH_MS,
  })

export const publicTargetStatsQuery = (targetId: string, window: StatsWindow) =>
  queryOptions({
    queryKey: ['public', 'targets', targetId, 'stats', window],
    queryFn: async () =>
      (await publicApi.get<UptimeStats>(`/targets/${targetId}/stats`, { params: { window } })).data,
    refetchInterval: LIVE_REFETCH_MS,
  })

export const publicTargetTimelineQuery = (targetId: string, window: StatsWindow) =>
  queryOptions({
    queryKey: ['public', 'targets', targetId, 'timeline', window],
    queryFn: async () =>
      (await publicApi.get<TimelinePoint[]>(`/targets/${targetId}/timeline`, { params: { window } })).data,
    refetchInterval: LIVE_REFETCH_MS,
  })
